import React, { createContext, useContext, useEffect, useState } from 'react';
import { api, getTenantSlug } from '../config/api';

export interface Tenant {
  institution_id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  cover_image_url: string | null;
}

interface TenantContextType {
  tenant: Tenant | null;
  isLoading: boolean;
  isTenantSubdomain: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = (): TenantContextType => {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within a TenantProvider');
  return ctx;
};

const cacheKey = (slug: string) => `vidya:tenant:${slug}`;

function readCache(slug: string | null): Tenant | null {
  if (!slug || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(cacheKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.institution_id) {
      return parsed as Tenant;
    }
    return null;
  } catch {
    return null;
  }
}

function writeCache(slug: string, tenant: Tenant | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (tenant) {
      window.localStorage.setItem(cacheKey(slug), JSON.stringify(tenant));
    } else {
      window.localStorage.removeItem(cacheKey(slug));
    }
  } catch {
    // localStorage can throw in private mode / quota — non-fatal.
  }
}

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const slug = getTenantSlug();
  const isTenantSubdomain = slug !== null;

  // Synchronous cache hydration — eliminates flash on return visits.
  const [tenant, setTenant] = useState<Tenant | null>(() => readCache(slug));
  const [isLoading, setIsLoading] = useState<boolean>(isTenantSubdomain && tenant === null);

  useEffect(() => {
    if (!isTenantSubdomain) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    api
      .get('/api/tenant/bootstrap')
      .then((res) => {
        if (cancelled) return;
        const data = res.data;
        if (data && data.institution_id) {
          const next: Tenant = {
            institution_id: data.institution_id,
            name: data.name,
            slug: data.slug,
            logo_url: data.logo_url ?? null,
            cover_image_url: data.cover_image_url ?? null,
          };
          setTenant(next);
          if (slug) writeCache(slug, next);
        } else {
          // Backend returned the same-shape null response (unknown / reserved slug).
          setTenant(null);
          if (slug) writeCache(slug, null);
        }
      })
      .catch(() => {
        // Bootstrap failures fall back to platform defaults — non-fatal.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, isTenantSubdomain]);

  return (
    <TenantContext.Provider value={{ tenant, isLoading, isTenantSubdomain }}>
      {children}
    </TenantContext.Provider>
  );
};
